import { AddIcon, DeleteIcon, EditIcon, RepeatIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Text,
  Tooltip
} from "@chakra-ui/react";
import { Popover } from "components/popover";
import DynamicTable from "components/dynamic-table";
import { GetLayout } from "components/get-layout";
import { useApplicationContext } from "contexts/ApplicationContext";
import { useEffect, useState } from "react";
import { TbCoin, TbCoinOff } from "react-icons/tb";
import { openModalConfirm } from "components/modal-confirm-new";
import { Toast } from "components/toast";
import { Filter2Icon } from "components/vectors/filter-icon";
import { NewDropdown } from "components/dropdown/new-dropdown";
import { RoutePermissionFunction } from "router-manager/parts";
import {
  ModalEmpresa,
  openModalEmpresa
} from "components/empresas-components/modal-empresa";
import { EmpresasEndpoints } from "components/empresas-components/utils";

interface Empresa {
  id: number;
  enabled: boolean;
  name: string;
  codigoNova: string;
  email: string;
  telefone: string;
  regionalCustomer: { id: number; name?: string };
  enabled3cplus: boolean;
  token3cplus: string;
  parentCustomer: { id: number };
  cnpj?: string;
  cashback?: "PENDING" | "ENABLED" | "DISABLED";
  codigoExterno?: string;
  comercialUser?: { id: string; name?: string };
}

interface Filtros {
  nomeOrCodigo: string;
}

interface Column {
  name: string;
  key?: string;
  render?: (row: Empresa) => any;
}

export default function Empresas() {
  const { user } = useApplicationContext();
  const { userData } = user;
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loadingCashbackPending, setLoadingCashbackPending] =
    useState<boolean>(false);
  const [cashbackFilter, setCashbackFilter] = useState<
    "PENDING" | "ENABLED" | "DISABLED" | null
  >(null);
  const [filtros, setFiltros] = useState<Filtros>({ nomeOrCodigo: "" });
  const [loading, setLoading] = useState<boolean>(false);

  const getFilteredEmpresas = () => {
    let filteredCustomers = [...empresas];
    if (filtros.nomeOrCodigo.trim() !== "") {
      filteredCustomers = filteredCustomers.filter(
        (c) =>
          (c.name || "")
            .toUpperCase()
            .includes(filtros.nomeOrCodigo.trim().toUpperCase()) ||
          (c.codigoNova || "")
            .toUpperCase()
            .includes(filtros.nomeOrCodigo.trim().toUpperCase()) ||
          (c.codigoExterno || "")
            .toUpperCase()
            .includes(filtros.nomeOrCodigo.trim().toUpperCase())
      );
    } else if (cashbackFilter !== null) {
      filteredCustomers = filteredCustomers.filter(
        (c) => c.cashback === cashbackFilter
      );
    }
    return filteredCustomers;
  };

  const onChangeFiltro = (
    field: keyof Filtros,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFiltros({ ...filtros, [field]: e.target.value });
  };

  const loadEmpresas = async () => {
    try {
      const data = await EmpresasEndpoints.getEmpresas();
      setEmpresas(data);
    } catch (error) {
      setEmpresas([]);
      Toast({ title: "Erro ao carregar empresas", status: "error" });
    }
  };

  const atualizar = async () => {
    setLoading(true);
    await loadEmpresas();
    setLoading(false);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await loadEmpresas();
      setLoading(false);
    };
    load();
  }, []);

  const submitCashbackStatus = async (
    status: "ENABLED" | "DISABLED",
    customerId: number
  ) => {
    try {
      setLoadingCashbackPending(true);
      await EmpresasEndpoints.changeCashbackStatus(customerId, status);
      const displayStatus = status === "ENABLED" ? "Ativo" : "Inativo";
      Toast({
        title: "Status do Cashback alterado para: " + displayStatus,
        status: "success"
      });
      await loadEmpresas();
    } catch (e) {
      console.log(e);
      Toast({ title: "Erro ao alterar status do Cashback para: " + status });
    } finally {
      setLoadingCashbackPending(false);
    }
  };

  const openNew = () => {
    openModalEmpresa({
      customerId: userData.customerId,
      userType: userData.type,
      onSaved: loadEmpresas
    });
  };

  const editEmpresa = (empresa: Empresa) => {
    openModalEmpresa({
      empresa: empresa as any,
      customerId: userData.customerId,
      userType: userData.type,
      onSaved: loadEmpresas
    });
  };

  const acceptDeleteEmpresa = async (rowData: Empresa) => {
    setLoading(true);
    try {
      await EmpresasEndpoints.deleteEmpresa(rowData.id);
    } catch (error) {
      Toast({
        title: "Erro ao excluir empresa!",
        status: "error"
      });
    }
    await loadEmpresas();
    setLoading(false);
  };

  const confirmDeleteEmpresa = (rowData: Empresa) => {
    openModalConfirm({
      message: `Deseja excluir "${rowData.name}"?`,
      onConfirm: () => acceptDeleteEmpresa(rowData),
      confirmButtonStyle: { variant: "danger", leftIcon: <DeleteIcon /> }
    });
  };

  const columns: Column[] = [
    {
      name: "Ativo",
      render: (row) => (row.enabled ? "SIM" : "NÃO")
    },
    {
      name: "Cód. Nova",
      key: "codigoNova"
    },
    ...(["SUPER", "PARCEIRO_GERENTE_VAREJO"].includes(userData.type!)
      ? [
        {
          name: "Cód. Externo",
          key: "codigoExterno"
        }
      ]
      : []),
    {
      name: "Nome",
      render: (row) =>
        row.name.length > 20 ? (
          <Tooltip label={row.name} bg="gray.200" color="text">
            <span>{row.name.slice(0, 20)}...</span>
          </Tooltip>
        ) : (
          row.name
        )
    },
    {
      name: "CNPJ/CPF",
      key: "cnpj"
    },
    {
      name: "Regional / Comercial",
      render: (row) => {
        const full = row.regionalCustomer?.name ?? row.comercialUser?.name ?? "";
        return full.length > 20 ? (
          <Tooltip label={full} bg="gray.200" color="text">
            <span>{full.slice(0, 20)}...</span>
          </Tooltip>
        ) : (
          full || null
        );
      }
    },
    {
      name: "Cashback",
      render: (row) =>
        row.cashback === "PENDING"
          ? "Pendente"
          : row.cashback === "ENABLED"
            ? "Ativo"
            : "Inativo"
    },
    {
      name: "Ações",
      render: (row) => {
        return (
          <Flex gap="5px">
            <IconButton
              icon={<EditIcon />}
              mr="8px"
              onClick={() => editEmpresa(row)}
              variant="outline"
              aria-label="Editar empresa"
            />
            {userData.type === "SUPER" || userData.type === "ASCOM" ? (
              <>
                <Tooltip label="Ativar Cashback" bg="gray.200" color="text">
                  <IconButton
                    icon={<TbCoin />}
                    onClick={() => submitCashbackStatus("ENABLED", row.id)}
                    mr="8px"
                    isDisabled={row.cashback === "ENABLED"}
                    color="green"
                    variant="outline"
                    isLoading={loadingCashbackPending}
                    aria-label="Ativar Cashback"
                  />
                </Tooltip>
                <Tooltip label="Inativar Cashback" bg="gray.200" color="text">
                  <IconButton
                    icon={<TbCoinOff />}
                    onClick={() => submitCashbackStatus("DISABLED", row.id)}
                    mr="8px"
                    isDisabled={row.cashback === "DISABLED"}
                    color="red"
                    variant="outline"
                    isLoading={loadingCashbackPending}
                    aria-label="Inativar Cashback"
                  />
                </Tooltip>
              </>
            ) : null}
            <IconButton
              icon={<DeleteIcon />}
              variant="outline"
              onClick={() => confirmDeleteEmpresa(row)}
              color="red"
              aria-label="Excluir empresa"
            />
          </Flex>
        );
      }
    }
  ];

  const cashbackList = [
    {
      name: "Selecione",
      value: null
    },
    {
      name: "Ativo",
      value: "ENABLED"
    },
    {
      name: "Inativo",
      value: "DISABLED"
    },
    {
      name: "Pendente",
      value: "PENDING"
    }
  ];

  const cashbackConvert = (
    value: "PENDING" | "ENABLED" | "DISABLED" | null | ""
  ) => {
    if (value === null || value === "") return "Selecione";
    if (value === "PENDING") return "Pendente";
    if (value === "ENABLED") return "Ativo";
    if (value === "DISABLED") return "Inativo";
  };

  return (
    <>
      {/* <ConfirmDialog dialogData={confirmDialogData} /> */}
      <GetLayout title="Empresas">
        <Flex
          w="100%"
          alignItems="center"
          justifyItems="center"
          gap="10px"
          mb="20px"
        >
          <Box w="25%">
            <Text mb="8px">Filtrar por Nome ou Código Nova:</Text>
            <Input
              value={filtros.nomeOrCodigo || ""}
              onChange={(e) => onChangeFiltro("nomeOrCodigo", e)}
            />
          </Box>
          <Box mt="30px">
            <Popover
              button={
                <Button
                  variant="ghost"
                  alignItems="center"
                  cursor="pointer"
                  _hover={{ bg: "gray.200" }}
                  px="10px"
                  borderRadius="10px"
                  leftIcon={<Filter2Icon />}
                  fontSize="14px"
                  fontWeight="medium"
                >
                  Filtrar por
                </Button>
              }
              title="Filtrar por"
              withOutCloseButton
              popupStyles={{ minWidth: "320px", minHeight: "150px" }}
              contentProps={{ padding: "0px" }}
            >
              {() => (
                <>
                  <Box overflow="auto" maxHeight="350px" padding="8px">
                    <Box mb="20px">
                      <Text fontSize="14px" fontWeight="medium" mb="8px">
                        Cashback
                      </Text>
                      <NewDropdown
                        options={cashbackList}
                        value={cashbackConvert(cashbackFilter)}
                        onChange={(e) => setCashbackFilter(e.value)}
                      />
                    </Box>
                  </Box>
                </>
              )}
            </Popover>
          </Box>
          <Box w="37.5%">
            <Text mb="8px">&nbsp;</Text>
            <Button w="100%" leftIcon={<AddIcon />} onClick={openNew}>
              Adicionar Empresa
            </Button>
          </Box>
          <Box w="37.5%">
            <Text mb="8px">&nbsp;</Text>
            <Button
              w="100%"
              leftIcon={<RepeatIcon />}
              onClick={atualizar}
              isLoading={loading}
              loadingText="Atualizando"
            >
              Atualizar
            </Button>
          </Box>
        </Flex>
        <DynamicTable
          rows={getFilteredEmpresas()}
          columns={columns}
          isLoading={loading}
        />
        <ModalEmpresa
          customerId={userData.customerId}
          userType={userData.type}
          onSaved={loadEmpresas}
        />
      </GetLayout>
    </>
  );
}

export const setRoutePermissions: RoutePermissionFunction = (route) => {
  route.setUserTypesPermission([
    "SUPER",
    "REGIONAL",
    "COMERCIAL",
    "PARCEIRO_GERENTE_ESTRATEGICO",
    "PARCEIRO_GERENTE_VAREJO",
    "FINANCEIRO",
    "ASCOM"
  ]);
  route.setColomboUserTypesPermission([
    "COMERCIAL",
    "PARCEIRO_GERENTE_ESTRATEGICO",
    "PARCEIRO_GERENTE_VAREJO"
  ]);
};
